import { BrowserRouter } from "react-router-dom"
import { QueryClient, QueryClientProvider } from "react-query"
import { ReactQueryDevtools } from "react-query/devtools"
import { ToastContainer } from "react-toastify"
import { AuthProvider } from "auth-provider"
import { AppState, Auth0Provider } from "@auth0/auth0-react"
import { useNavigate } from "react-router-dom"
import AllRoutes from "./index"
import "react-toastify/dist/ReactToastify.css"
import { isLocal, getAuth0Params, getHostInfo } from "../utils/networking"

// Paths that should be ignored for Auth0 callback handling
const IGNORE_CALLBACK_PATHS = ["/logout", "/freight_bill", "/invoice"]

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
})

const Auth0ProviderWithNavigate = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const navigate = useNavigate()
  const { tenant } = getHostInfo()
  const { domain, clientId } = getAuth0Params()

  // Check if current path should ignore Auth0 callbacks
  const shouldSkipRedirectCallback = IGNORE_CALLBACK_PATHS.some((path) =>
    window.location.pathname.includes(path)
  )

  const onRedirectCallback = (appState?: AppState) => {
    const returnTo = appState?.returnTo || window.location.pathname
    if (returnTo.includes("/logout")) return
    navigate(returnTo)
  }

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      useRefreshTokens
      useRefreshTokensFallback
      cacheLocation="localstorage"
      cookieDomain={
        isLocal(window.location.host)
          ? undefined
          : `.${window.location.host.split(".").slice(-2).join(".")}`
      }
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: "dashfuel_api",
        ...(tenant !== "login" && { organization: tenant }),
        scope: "profile email openid phone offline_access",
      }}
      skipRedirectCallback={shouldSkipRedirectCallback}
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  )
}

const RoutesContainer = () => {
  return (
    <BrowserRouter>
      <Auth0ProviderWithNavigate>
        <AuthProvider>
          <AllRoutes />
          <ToastContainer />
        </AuthProvider>
      </Auth0ProviderWithNavigate>
    </BrowserRouter>
  )
}

const Routes = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <RoutesContainer />
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}

export default Routes
